import { JAW_TYPES, TEETH_STATUSES } from '@/shared/constants'
import {
  JawTypesType,
  PeriodontalDataType,
  PeriodontalDiagramType,
} from '@/shared/types'

const UP_TEETH_IDS = [
  18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28,
]
const DOWN_TEETH_IDS = [
  48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38,
]
const TEETH_WITH_FURATION = [18, 17, 16, 26, 27, 28, 48, 47, 46, 36, 37, 38]

export interface PeriodontalDataInterface {
  setData(periodontalData: PeriodontalDataType[]): void
  getData(): PeriodontalDataType[]
  getActivePlaque(): number
  getActiveBleeding(): number
  getSoundingValue(value: number, isMoreValues?: boolean): number
  changeStatus(itemId: number): void
  resetData(itemId: number): void
  getDiagramData(): PeriodontalDiagramType
  changeMobility(itemId: number): void
  changeFuration(itemId: number, furationId: number): void
  changePlaque(itemId: number, plaqueId: number): void
  changeBleeding(itemId: number, bleedingId: number): void
  changeSuppuration(itemId: number, suppurationId: number): void
  changeRecession(itemId: number, recessionId: number, value: number): void
  changeSounding(itemId: number, soundingId: number, value: number): void
}

export class PeriodontalData implements PeriodontalDataInterface {
  private periodontalData: PeriodontalDataType[] = []
  private isDoubleFuration: boolean

  constructor(jawType: JawTypesType, doubleFuration = false) {
    this.isDoubleFuration = doubleFuration

    if (jawType === JAW_TYPES.jawUp) {
      this.initUpTeethData()
    } else if (JAW_TYPES.jawDown) {
      this.initDownTeethData()
    }
  }

  public setData(periodontalData: PeriodontalDataType[]): void {
    for (let i = 0; i < periodontalData.length; i++) {
      if (periodontalData[i].suppuration === undefined) {
        periodontalData[i].suppuration = [false, false, false]
      }
    }
    this.periodontalData = periodontalData
  }

  public getData(): PeriodontalDataType[] {
    return this.periodontalData
  }

  public getActivePlaque(): number {
    let totalActivePlaque = 0

    this.periodontalData.forEach((data) => {
      data.plaque.forEach((value) => {
        if (value) {
          totalActivePlaque++
        }
      })
    })

    return totalActivePlaque
  }

  public getActiveBleeding(): number {
    let totalActiveBleeding = 0

    this.periodontalData.forEach((data) => {
      data.bleeding.forEach((value) => {
        if (value) {
          totalActiveBleeding++
        }
      })
    })

    return totalActiveBleeding
  }

  public getSoundingValue(value: number, isMoreValues = false): number {
    let totalSounding = 0

    this.periodontalData.forEach((data) => {
      data.sounding.forEach((val) => {
        if (isMoreValues) {
          if (val >= value) {
            totalSounding++
          }
        } else {
          if (val === value) {
            totalSounding++
          }
        }
      })
    })

    return totalSounding
  }

  public changeStatus(itemId: number): void {
    const index = this.periodontalData.findIndex(
      (item) => item.number === itemId
    )

    let statusCode = this.periodontalData[index].statusCode

    if (statusCode !== TEETH_STATUSES.implant) {
      this.periodontalData[index].statusCode++
      statusCode++
    } else {
      this.periodontalData[index].statusCode = TEETH_STATUSES.normal
      statusCode = TEETH_STATUSES.normal
    }

    if (statusCode === TEETH_STATUSES.implant) {
      this.getDiagramDataFromBuffer(index)
    }

    if (statusCode === TEETH_STATUSES.removed) {
      this.bufferDiagramData(index)
    }
  }

  public resetData(itemId: number): void {
    const index = this.periodontalData.findIndex(
      (item) => item.number === itemId
    )
    this.periodontalData[index].mobility = 0
    this.periodontalData[index].furation = this.periodontalData[
      index
    ].furation.map(() => 0)

    this.periodontalData[index].statusCode = 0
    this.periodontalData[index].recession = [0, 0, 0]
    this.periodontalData[index].sounding = [0, 0, 0]
    this.periodontalData[index].recessionBuffer = [0, 0, 0]
    this.periodontalData[index].soundingBuffer = [0, 0, 0]
    this.periodontalData[index].plaque = [false, false, false]
    this.periodontalData[index].bleeding = [false, false, false]
    this.periodontalData[index].suppuration = [false, false, false]
  }

  public getDiagramData(): PeriodontalDiagramType {
    const recession = this.getRecession()
    const sounding = this.getSounding()
    const removedTooth = this.getRemovedTooth()
    const implantTooth = this.getImplantTooth()

    return {
      recession,
      sounding,
      removedTooth,
      implantTooth,
    }
  }

  public changeMobility(itemId: number): void {
    const index = this.periodontalData.findIndex(
      (item) => item.number === itemId
    )

    if (this.periodontalData[index].mobility === 3) {
      this.periodontalData[index].mobility = 0
    } else {
      this.periodontalData[index].mobility++
    }
  }

  public changeFuration(itemId: number, furationId: number): void {
    const index = this.periodontalData.findIndex(
      (item) => item.number === itemId
    )

    if (this.periodontalData[index].furation[furationId] === 3) {
      this.periodontalData[index].furation[furationId] = 0
    } else {
      this.periodontalData[index].furation[furationId]++
    }
  }

  public changePlaque(itemId: number, plaqueId: number): void {
    const index = this.periodontalData.findIndex(
      (item) => item.number === itemId
    )

    this.periodontalData[index].plaque[plaqueId] =
      !this.periodontalData[index].plaque[plaqueId]
  }

  public changeBleeding(itemId: number, bleedingId: number): void {
    const index = this.periodontalData.findIndex(
      (item) => item.number === itemId
    )
    console.debug('change bleeding with index =', index)
    this.periodontalData[index].bleeding[bleedingId] =
      !this.periodontalData[index].bleeding[bleedingId]
  }

  public changeSuppuration(itemId: number, suppurationId: number): void {
    const index = this.periodontalData.findIndex(
      (item) => item.number === itemId
    )
    console.debug('change suppuration with index =', index)
    this.periodontalData[index].suppuration[suppurationId] =
      !this.periodontalData[index].suppuration[suppurationId]
  }

  public changeRecession(
    itemId: number,
    recessionId: number,
    value: number
  ): void {
    const index = this.periodontalData.findIndex(
      (item) => item.number === itemId
    )

    this.periodontalData[index].recession[recessionId] = value
  }

  public changeSounding(
    itemId: number,
    soundingId: number,
    value: number
  ): void {
    const index = this.periodontalData.findIndex(
      (item) => item.number === itemId
    )

    this.periodontalData[index].sounding[soundingId] = value
  }

  private initUpTeethData(): void {
    UP_TEETH_IDS.forEach((id) => {
      this.periodontalData.push(this.getInitialDataItem(id))
    })
  }

  private initDownTeethData(): void {
    DOWN_TEETH_IDS.forEach((id) => {
      this.periodontalData.push(this.getInitialDataItem(id))
    })
  }

  private getInitialDataItem(id: number): PeriodontalDataType {
    let furation: number[] = []

    if (this.checkIsNeedInsertFuration(id)) {
      if (this.isDoubleFuration) {
        furation = [0, 0]
      } else {
        furation = [0]
      }
    }

    const periodontalDataItem: PeriodontalDataType = {
      number: id,
      mobility: 0,
      furation,
      plaque: [false, false, false],
      bleeding: [false, false, false],
      suppuration: [false, false, false],
      recession: [0, 0, 0],
      sounding: [0, 0, 0],
      recessionBuffer: [0, 0, 0],
      soundingBuffer: [0, 0, 0],
      statusCode: TEETH_STATUSES.normal,
    }

    return periodontalDataItem
  }

  private checkIsNeedInsertFuration(id: number): boolean {
    return (
      TEETH_WITH_FURATION.includes(id) ||
      ((id === 14 || id === 24) && this.isDoubleFuration)
    )
  }

  private getDiagramDataFromBuffer(bufferedItemId: number): void {
    this.periodontalData[bufferedItemId].recession = [
      ...this.periodontalData[bufferedItemId].recessionBuffer,
    ]
    this.periodontalData[bufferedItemId].sounding = [
      ...this.periodontalData[bufferedItemId].soundingBuffer,
    ]
  }

  private bufferDiagramData(bufferedItemId: number): void {
    this.periodontalData[bufferedItemId].recessionBuffer = [
      ...this.periodontalData[bufferedItemId].recession,
    ]
    this.periodontalData[bufferedItemId].soundingBuffer = [
      ...this.periodontalData[bufferedItemId].sounding,
    ]

    this.periodontalData[bufferedItemId].recession = [0, 0, 0]
    this.periodontalData[bufferedItemId].sounding = [0, 0, 0]
  }

  private getRecession(): number[] {
    const recession: number[] = []

    this.periodontalData.forEach((data) => {
      data.recession.forEach((item) => {
        recession.push(item)
      })
    })

    return recession
  }

  private getSounding(): number[] {
    const sounding: number[] = []

    this.periodontalData.forEach((data) => {
      data.sounding.forEach((item) => {
        sounding.push(item)
      })
    })

    return sounding
  }

  private getRemovedTooth(): number[] {
    const tooth: number[] = []

    this.periodontalData.forEach((data) => {
      if (data.statusCode === TEETH_STATUSES.removed) {
        tooth.push(data.number)
      }
    })

    return tooth
  }

  private getImplantTooth(): number[] {
    const tooth: number[] = []

    this.periodontalData.forEach((data) => {
      if (data.statusCode === TEETH_STATUSES.implant) {
        tooth.push(data.number)
      }
    })

    return tooth
  }
}
